import axios from 'axios';
import get from 'lodash/get';
import { put, select, call, takeEvery, all, takeLatest } from 'redux-saga/effects';

import {
  filterSelector,
  intervalSelector,
  selectedKPIConfigSelector,
  kpiMetricSelector,
} from '@store/overviewDashboard/base/selectors';
import dimensionStoreSelector, {
  benchmarkTableColumnsSelector,
  supportedMetricsSelector,
} from './selectors';
import { activeTabSelector } from '@store/overviewDashboard/kpiData/selectors';
import * as baseTypes from '../base/types';
import * as actions from './actions';
import * as types from './types';
import * as kpiTypes from '@store/overviewDashboard/kpiData/types';
import * as dimTypes from '@store/overviewDashboard/dimensionData/types';
import { buildBenchmarkQuery, createDimension } from './helper';
import { getBenchmarkData, getSupportedMetrics } from '@api/benchmark';
import {
  BM_SUPPORTED_ADVANCED_DIMENSIONS,
  IMPROVEMENT_OPPORTUNITY_METRICS,
  SPI_BEST_METRICS,
} from '@src/utils/kpi';
import { BenchmarkTabKey } from '@store/overviewDashboard/kpiData/reducer';
import { metricKeys } from '@pulse/metrics';
import { autoRequest } from '@src/api/createRequestStore';
import { advanceBenchmarkSelector, hasBenchmarkSelector } from '@store/common/selectors';
import {
  isKpiValid,
  benchmarkMetricFinder,
} from '@components/overview-dashboard/metrics-container/sub-opportunities/Helper';
import { isNonBenchmarkFilterApplied } from '@components/overview-dashboard/dimension-container/dimension-table/utils';

function* initRequest() {
  yield put(actions.init());
}

function* getSupportedBenchmarkMetrics() {
  const hasBenchmarkFlag = yield select(hasBenchmarkSelector);
  if (!hasBenchmarkFlag) {
    return;
  }

  let supportedMetricsStore = yield select(supportedMetricsSelector);
  if (supportedMetricsStore.response === null) {
    yield fetchSupportedBenchmarkMetrics();
    supportedMetricsStore = yield select(supportedMetricsSelector);
  }
  const supportedMetrics = get(supportedMetricsStore, ['response', 'data', 'data'], []);

  return supportedMetrics;
}

function* fetchSupportedBenchmarkMetrics() {
  const storeType = types.SUPPORTED_METRICS;
  yield put(actions.fetchSupportedMetrics());
  try {
    yield autoRequest(getSupportedMetrics, storeType);
  } catch (error) {
    // ignore
  }
}

function* initBenchmarkSPI() {
  const hasBenchmarkFlag = yield select(hasBenchmarkSelector);
  if (!hasBenchmarkFlag) {
    return;
  }
  yield fetchBenchmarkSPIData();
}

function* initBenchmarkKPI() {
  const hasBenchmarkFlag = yield select(hasBenchmarkSelector);
  if (!hasBenchmarkFlag) {
    return;
  }
  const selectedKPICofig = yield select(selectedKPIConfigSelector);

  const activeTab = yield select(activeTabSelector);

  let metrics = IMPROVEMENT_OPPORTUNITY_METRICS;
  if (activeTab === BenchmarkTabKey) {
    // ADD KPI BEST metricKeys
    if (selectedKPICofig.kpiID === 2) {
      metrics = SPI_BEST_METRICS;
    }
    yield fetchBenchmarkKPIData(metrics);
  }
}

function* fetchBenchmarkKPIData(columns) {
  const appliedFilter = yield select(filterSelector);
  const interval = yield select(intervalSelector);
  const supportedMetrics = yield getSupportedBenchmarkMetrics();
  const storeType = types.IMP_BENCHMARK_KPI;
  const payload = buildBenchmarkQuery({
    filter: appliedFilter.rule,
    interval: `${interval.from}/${interval.to}`,
    columns,
    supportedMetrics,
    dimension: undefined,
  });

  const selectedKPICofig = yield select(selectedKPIConfigSelector);
  const isKpiFound = isKpiValid(selectedKPICofig);
  if (!isKpiFound) {
    yield put(actions.abortKPIFetching());
    yield put(actions.setKPIResponse(null));
  } else if (payload !== null) {
    yield put(actions.setKPIFetchingState());
    try {
      yield autoRequest(getBenchmarkData, storeType, payload);
    } catch (error) {
      yield put(actions.setKPIResponse(null));
    }
  }
}

function* fetchBenchmarkSPIData() {
  const appliedFilter = yield select(filterSelector);
  const interval = yield select(intervalSelector);
  const selectedKPICofig = yield select(selectedKPIConfigSelector);
  let columns = [metricKeys.StreamingPerformanceIndex];
  // Conviva Best condition
  if (selectedKPICofig && selectedKPICofig.kpiID === 2) {
    columns = [metricKeys.StreamingPerformanceIndexBest];
  }
  const supportedMetrics = yield getSupportedBenchmarkMetrics();

  const payload = buildBenchmarkQuery({
    filter: appliedFilter.rule,
    interval: `${interval.from}/${interval.to}`,
    columns,
    supportedMetrics,
    dimension: undefined,
  });
  const storeType = types.IMP_BENCHMARK_SPI;

  const isKpiFound = isKpiValid(selectedKPICofig);
  const nonBenchmarkFilterApplied = isNonBenchmarkFilterApplied(get(appliedFilter, 'rule', []));
  if (!isKpiFound || nonBenchmarkFilterApplied) {
    yield put(actions.abortSPIFetching());
    yield put(actions.setSPIResponse(null));
  } else if (payload !== null) {
    yield put(actions.setSPIFetchingState());
    try {
      yield autoRequest(getBenchmarkData, storeType, payload);
    } catch (error) {
      yield put(actions.setSPIResponse(null));
      const errorMsg = error.message || 'Unknown error!';
    }
  }
}

function* initBenchmark(action) {
  const hasBenchmarkFlag = yield select(hasBenchmarkSelector);
  const advancedBenchmarkFlag = yield select(advanceBenchmarkSelector);
  const { dimension } = action;
  if (!hasBenchmarkFlag) {
    return;
  }
  if (BM_SUPPORTED_ADVANCED_DIMENSIONS.includes(dimension) && !advancedBenchmarkFlag) {
    return;
  }

  yield fetchDimensionBenchmarkData({ dimension });
}

function* fetchDimensionBenchmarkData(params) {
  const { dimension } = params;
  const appliedFilter = yield select(filterSelector);
  const interval = yield select(intervalSelector);
  const selectedKPICofig = yield select(selectedKPIConfigSelector);
  const selectedMetric = yield select(kpiMetricSelector);
  let columns = yield select(benchmarkTableColumnsSelector);

  const dimensionStore = createDimension(dimension);
  yield put(actions.setupDimension({ dimension, dimensionStore }));
  // Conviva Best condition for default Dimensions
  // We dont have benchmark gateway metrics support in MGW we need to call them directly
  if (selectedKPICofig.kpiID === 2) {
    columns = [benchmarkMetricFinder(selectedMetric) || metricKeys.StreamingPerformanceIndexBest];
  }
  const supportedMetrics = yield getSupportedBenchmarkMetrics();
  const isKpiFound = isKpiValid(selectedKPICofig);

  const query = buildBenchmarkQuery({
    filter: appliedFilter.rule,
    interval: `${interval.from}/${interval.to}`,
    dimension,
    columns,
    supportedMetrics,
  });

  // When it is not CONVIVA_GOOD/BEST, we need to hide benchmark
  // but state should have dimenstions.
  if (!isKpiFound) {
    yield put(
      actions.setResponse({
        dimension,
        response: null,
        errorMsg: '',
      }),
    );
  } else if (query !== null) {
    try {
      const source = axios.CancelToken.source();
      const response = yield call(getBenchmarkData, query, { cancelToken: source.token });
      yield put(
        actions.setResponse({
          dimension,
          response: get(response, 'data["benchmarkData"]', null),
          errorMsg: '',
        }),
      );
    } catch (error) {
      const errorMsg = error.message || 'Unknown error!';
      yield put(actions.setResponse({ dimension, response: null, errorMsg }));
    }
  }
}

function* refreshDimensions() {
  const hasBenchmarkFlag = yield select(hasBenchmarkSelector);
  const appliedFilter = yield select(filterSelector);
  if (!hasBenchmarkFlag) {
    return;
  }
  if (isNonBenchmarkFilterApplied(get(appliedFilter, 'rule', []))) {
    return;
  }

  const { benchmark } = yield select(dimensionStoreSelector);
  const tasks = Object.keys(benchmark).map((dimension) =>
    call(fetchDimensionBenchmarkData, { dimension }),
  );
  yield all(tasks);
}

function* watchSelectedKpiMetric() {
  yield takeLatest(
    [
      baseTypes.SET_SELECTED_KPI,
      baseTypes.SET_SELECTED_KPI_CONFIG,
      baseTypes.APPLY_FILTER,
      baseTypes.SET_INTERVAL,
    ],
    refreshDimensions,
  );
}

function* watchInitRequest() {
  yield takeEvery([types.INIT], initRequest);
}
// used for reload/retry
function* watchFetchData() {
  yield takeEvery(dimTypes.FETCH_DATA, fetchDimensionBenchmarkData);
}

function* watchInitBenchmark() {
  yield takeEvery(types.INIT_BENCHMARK, initBenchmark);
}

function* watchInitBenchmarkSPI() {
  yield takeLatest(
    [
      types.INIT_BENCHMARK_SPI,
      baseTypes.SET_SELECTED_KPI_CONFIG,
      baseTypes.SET_INTERVAL,
      baseTypes.APPLY_FILTER,
    ],
    initBenchmarkSPI,
  );
}

function* watchInitBenchmarkKPI() {
  yield takeLatest(
    [
      kpiTypes.INIT,
      baseTypes.SET_INTERVAL,
      baseTypes.APPLY_FILTER,
      baseTypes.SET_SELECTED_KPI_CONFIG,
      kpiTypes.SET_ACTIVE_TAB,
      kpiTypes.FETCH_DATA,
    ],
    initBenchmarkKPI,
  );
}

export default [
  watchInitBenchmark,
  watchInitRequest,
  watchSelectedKpiMetric,
  watchInitBenchmarkSPI,
  watchInitBenchmarkKPI,
  watchFetchData,
];
